// src/components/AdminSettings/tabs/FamilyTab.tsx
import React from 'react';
import { add } from 'ionicons/icons';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { List, type ListItemProps } from '../../ui/List';
import { Badge } from '../../ui/Badge';
import { ClickableAvatar } from '../../ui/AvatarPicker';
import { MEMBER_COLORS } from '../../ui/ColorPicker';
import { RELATIONSHIP_OPTIONS } from '../../../types/user';
import type { FamilyMember, RelationshipType } from '../../../types';
import type { FamilyTabProps } from '../types';

// Get display name for member (nickname takes priority, fallback to name, then 'Unnamed')
const getMemberDisplayName = (member: FamilyMember): string => {
  if (member.nickname && member.nickname.trim()) {
    return member.nickname.trim();
  }
  if (member.name && member.name.trim()) {
    return member.name.trim();
  }
  return 'Unnamed Member';
};

// Get relationship label
const getRelationshipLabel = (relationship?: RelationshipType) => {
  if (!relationship) return null;
  const option = RELATIONSHIP_OPTIONS.find(r => r.value === relationship);
  return option?.label || relationship;
};

export const FamilyTab: React.FC<FamilyTabProps> = ({
  familyMembers,
  onAddMember,
  onEditMember,
  onEditMemberAvatar,
}) => {

  // Convert family members to List items
  const familyMemberItems: ListItemProps[] = familyMembers.map(member => {
    const displayName = getMemberDisplayName(member);
    // Show full name as description only if nickname is being used
    const showFullName = member.nickname && member.nickname.trim() && member.name && member.name !== member.nickname;
    const memberColor = member.color || MEMBER_COLORS[0];

    return {
      id: member.id,
      label: displayName,
      description: showFullName ? member.name : undefined,
      start: {
        element: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Color accent stripe */}
            <div
              style={{
                width: 4,
                height: 44,
                borderRadius: 2,
                backgroundColor: memberColor,
                flexShrink: 0,
              }}
            />
            {/* Clickable Avatar - wrap in div to stop propagation to list item */}
            <div onClick={(e) => { e.stopPropagation(); onEditMemberAvatar(member); }}>
              <ClickableAvatar
                avatarUrl={member.avatar_url}
                fallback={member.avatar_emoji || '👤'}
                size={44}
                backgroundColor={memberColor}
                showEditOverlay
              />
            </div>
          </div>
        ),
      },
      end: {
        element: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {member.relationship && (
              <Badge variant="neutral" size="sm">{getRelationshipLabel(member.relationship)}</Badge>
            )}
            {member.is_parent && (
              <Badge variant="primary" size="sm">Parent</Badge>
            )}
          </div>
        ),
      },
      onClick: () => onEditMember(member),
    };
  });

  return (
    <>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <h3 style={{
          fontFamily: 'var(--kff-heading-font)',
          fontSize: 16,
          fontWeight: 600,
          margin: 0,
        }}>
          Family Members
        </h3>
        <Button variant="primary" size="small" iconStart={add} onClick={onAddMember}>
          Add Member
        </Button>
      </div>

      {familyMemberItems.length > 0 ? (
        <List items={familyMemberItems} variant="default" size="md" lines="inset" />
      ) : (
        <Card style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--ion-color-medium)', marginBottom: 16 }}>
            No family members yet
          </p>
          <Button variant="primary" onClick={onAddMember}>
            Add First Member
          </Button>
        </Card>
      )}
    </>
  );
};

export default FamilyTab;
